/* FundsTab — manage NHF's restricted/unrestricted funds + see per-fund balances.
 * Each fund has its own revenue, expenses, opening balance, and available figure.
 * System (seeded) funds cannot be hard-deleted; can be deactivated.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Coins, Plus, Loader2, Edit3, Save, X, Lock, Power, PowerOff, RefreshCw,
  TrendingUp, TrendingDown, Star, Wallet, Heart, Target, BarChart3, FileSpreadsheet,
  Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FUND_TYPE_BADGE = {
  unrestricted: 'bg-blue-50 text-blue-800 border-blue-200',
  temporarily_restricted: 'bg-amber-50 text-amber-800 border-amber-200',
  permanently_restricted: 'bg-rose-50 text-rose-800 border-rose-200',
  board_designated: 'bg-indigo-50 text-indigo-800 border-indigo-200',
};

const FUND_TYPE_LABEL = {
  unrestricted: 'Unrestricted',
  temporarily_restricted: 'Temporarily Restricted',
  permanently_restricted: 'Permanently Restricted',
  board_designated: 'Board-Designated',
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const FundsTab = () => {
  const [funds, setFunds] = useState([]);
  const [balances, setBalances] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newForm, setNewForm] = useState({
    code: '', name: '', fund_type: 'temporarily_restricted',
    description: '', reporting_notes: '',
  });
  const [designationSetting, setDesignationSetting] = useState({ enabled: false, intro_copy: '' });
  const [savingDesignation, setSavingDesignation] = useState(false);

  const loadDesignationSetting = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/site/donate-fund-designation`, { withCredentials: true });
      setDesignationSetting({
        enabled: !!r.data?.enabled,
        intro_copy: r.data?.intro_copy || '',
      });
    } catch (e) { /* non-critical */ }
  }, []);

  const saveDesignation = async (next) => {
    setSavingDesignation(true);
    try {
      const r = await axios.put(`${BACKEND_URL}/api/admin/site/donate-fund-designation`,
        next, { withCredentials: true });
      setDesignationSetting((s) => ({ ...s, enabled: !!r.data?.enabled }));
    } catch (e) { alert(e.response?.data?.detail || 'Save failed.'); }
    setSavingDesignation(false);
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [a, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/funds?include_inactive=${showInactive}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/funds/balances`, { withCredentials: true }),
      ]);
      setFunds(a.data?.funds || []);
      setBalances(b.data || { rows: [], totals: {} });
    } catch (e) { setError(e.response?.data?.detail || 'Could not load funds.'); }
    setLoading(false);
  }, [showInactive]);

  useEffect(() => { load(); loadDesignationSetting(); }, [load, loadDesignationSetting]);

  const balanceFor = (fundId) => balances.rows?.find((r) => r.fund_id === fundId) || {};

  const createNew = async () => {
    if (!newForm.code.trim() || !newForm.name.trim()) {
      alert('Code and name are required.'); return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/funds`, newForm, { withCredentials: true });
      setShowNew(false);
      setNewForm({ code: '', name: '', fund_type: 'temporarily_restricted', description: '', reporting_notes: '' });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Create failed.'); }
  };

  const saveEdit = async (fundId) => {
    try {
      const payload = { ...editForm };
      // Clean impact_metrics
      if (Array.isArray(payload.impact_metrics)) {
        payload.impact_metrics = payload.impact_metrics
          .filter((m) => (m.label || '').trim())
          .map((m) => ({
            id: m.id, label: m.label.trim(),
            unit: m.unit || null,
            target_value: m.target_value === '' ? null : Number(m.target_value),
            current_value: Number(m.current_value || 0),
            notes: m.notes || null,
          }));
      }
      if (payload.budget_target === '' || payload.budget_target == null) {
        payload.budget_target = null;
      } else {
        payload.budget_target = Number(payload.budget_target);
      }
      await axios.put(`${BACKEND_URL}/api/admin/funds/${fundId}`, payload, { withCredentials: true });
      setEditingId(null);
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed.'); }
  };

  const downloadFundCsv = (f) => {
    window.open(`${BACKEND_URL}/api/admin/funds/${f.id}/report.csv`, '_blank');
  };

  const toggleActive = async (f) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/funds/${f.id}`,
        { is_active: !f.is_active }, { withCredentials: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Toggle failed.'); }
  };

  const setDefault = async (f) => {
    if (!window.confirm(`Make "${f.name}" the default unrestricted fund? Untagged donations will be assigned here.`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/funds/${f.id}`,
        { default_for_unrestricted: true }, { withCredentials: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to set default.'); }
  };

  return (
    <div className="space-y-4" data-testid="funds-tab">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#BA1826]" /> Funds
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            Funds let NHF track money by restriction class (FASB ASC 958) and program designation.
            Donor-restricted gifts (e.g. <span className="font-semibold">Food Assistance, Haiti Relief, CPR/AED</span>)
            stay sequestered; unrestricted dollars flow freely. Untagged donations roll up to the default
            fund <Star className="inline w-3 h-3 text-[#BA1826]" />.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-[#4B5563] inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="funds-show-inactive" /> Show inactive
          </label>
          <button onClick={load} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="funds-refresh"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={() => setShowNew(true)}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1.5"
            data-testid="funds-new-btn">
            <Plus className="w-3 h-3" /> New fund
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="funds-totals">
        <Tile icon={Wallet}      label="Opening balance" value={fmtMoney(balances.totals?.opening_balance)} accent="indigo" />
        <Tile icon={TrendingUp}  label="Revenue (all funds)" value={fmtMoney(balances.totals?.revenue)} accent="green" />
        <Tile icon={TrendingDown} label="Expenses (all funds)" value={fmtMoney(balances.totals?.expenses)} accent="amber" />
        <Tile icon={Coins}       label="Available across funds" value={fmtMoney(balances.totals?.available)} accent="brand" />
      </div>

      {/* Donor designation toggle (Phase I.4) */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="donate-designation-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-[#0A0A0A] inline-flex items-center gap-1.5">
              <Heart className="w-3.5 h-3.5 text-[#BA1826]" /> Public Donate-page fund designation
            </p>
            <p className="text-[11px] text-[#4B5563] mt-1 max-w-2xl">
              When enabled, the public Donate page shows an optional "Designate your gift" dropdown
              listing every active fund. Donors who pick one have their gift auto-tagged to that
              fund (visible on the Unified Donations Ledger + Statement of Activities). Leave off
              for unrestricted-only campaigns.
            </p>
          </div>
          <button onClick={() => saveDesignation({ enabled: !designationSetting.enabled, intro_copy: designationSetting.intro_copy })}
            disabled={savingDesignation}
            className={`relative w-12 h-6 rounded-full transition-colors ${designationSetting.enabled ? 'bg-[#BA1826]' : 'bg-[#E5E7EB]'}`}
            data-testid="donate-designation-toggle"
            aria-label="Toggle donor fund designation on public Donate page">
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${designationSetting.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
        {designationSetting.enabled && (
          <div className="mt-2 pt-2 border-t border-[#F1F5F9]">
            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">
              Intro copy shown above the picker
            </label>
            <div className="flex items-center gap-2">
              <input value={designationSetting.intro_copy}
                onChange={(e) => setDesignationSetting((s) => ({ ...s, intro_copy: e.target.value }))}
                placeholder="Optional — pick the program your gift supports. Leave blank for general support."
                className="flex-1 px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
                data-testid="donate-designation-intro" />
              <button onClick={() => saveDesignation({ enabled: true, intro_copy: designationSetting.intro_copy })}
                disabled={savingDesignation}
                className="text-[10px] px-2 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1"
                data-testid="donate-designation-save-copy">
                {savingDesignation ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New fund form */}
      {showNew && (
        <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3 space-y-2" data-testid="funds-new-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#BA1826]">New fund</p>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-[#4B5563]" /></button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <input value={newForm.code}
              onChange={(e) => setNewForm({ ...newForm, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 20) })}
              placeholder="Code (e.g. WATER)" className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs font-mono"
              data-testid="new-fund-code" />
            <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="Fund name" className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs lg:col-span-2"
              data-testid="new-fund-name" />
            <select value={newForm.fund_type} onChange={(e) => setNewForm({ ...newForm, fund_type: e.target.value })}
              className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs" data-testid="new-fund-type">
              <option value="unrestricted">Unrestricted</option>
              <option value="temporarily_restricted">Temporarily Restricted</option>
              <option value="permanently_restricted">Permanently Restricted</option>
              <option value="board_designated">Board-Designated</option>
            </select>
          </div>
          <textarea value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
            placeholder="What is this fund for? (donor-facing description)"
            className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs" rows={2}
            data-testid="new-fund-description" />
          <textarea value={newForm.reporting_notes} onChange={(e) => setNewForm({ ...newForm, reporting_notes: e.target.value })}
            placeholder="Internal: grant restrictions, reporting cadence, impact metrics to capture…"
            className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs" rows={2}
            data-testid="new-fund-notes" />
          <div className="flex justify-end">
            <button onClick={createNew}
              className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5"
              data-testid="new-fund-save"><Save className="w-3 h-3" /> Create fund</button>
          </div>
        </div>
      )}

      {/* Funds list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] text-[#9CA3AF]">
              <tr>
                <th className="text-left px-3 py-2 w-20">Code</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-40">Type</th>
                <th className="text-right px-3 py-2 w-28">Revenue</th>
                <th className="text-right px-3 py-2 w-28">Expenses</th>
                <th className="text-right px-3 py-2 w-28">Available</th>
                <th className="text-right px-3 py-2 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {funds.map((f) => {
                const bal = balanceFor(f.id);
                if (editingId === f.id) {
                  return (
                    <tr key={f.id} className="border-t border-[#E5E7EB] bg-[#FFF8F8]"
                      data-testid={`fund-row-${f.code}-editing`}>
                      <td colSpan={7} className="px-3 py-3">
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">Code</label>
                              <div className="px-2 py-1 font-mono font-bold text-xs">{f.code}</div>
                            </div>
                            <div className="lg:col-span-2">
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">Name</label>
                              <input value={editForm.name || ''}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs"
                                data-testid={`fund-edit-name-${f.code}`} />
                            </div>
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">Type</label>
                              <select value={editForm.fund_type || f.fund_type}
                                onChange={(e) => setEditForm({ ...editForm, fund_type: e.target.value })}
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs">
                                <option value="unrestricted">Unrestricted</option>
                                <option value="temporarily_restricted">Temp Restricted</option>
                                <option value="permanently_restricted">Perm Restricted</option>
                                <option value="board_designated">Board-Designated</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5 inline-flex items-center gap-1">
                                <Target className="w-3 h-3" /> Budget target (USD)
                              </label>
                              <input type="number" min="0" step="0.01"
                                value={editForm.budget_target ?? ''}
                                onChange={(e) => setEditForm({ ...editForm, budget_target: e.target.value })}
                                placeholder="0.00 (none)"
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono"
                                data-testid={`fund-edit-budget-${f.code}`} />
                            </div>
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">Start date</label>
                              <input type="date" value={editForm.start_date || ''}
                                onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                            </div>
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">End date</label>
                              <input type="date" value={editForm.end_date || ''}
                                onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                            </div>
                            <div>
                              <label className="block text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-0.5">Description</label>
                              <input value={editForm.description ?? f.description ?? ''}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                            </div>
                          </div>

                          {/* Impact metrics editor */}
                          <div className="bg-white border border-[#E5E7EB] rounded p-2">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-[10px] uppercase font-bold tracking-wider text-[#4B5563] inline-flex items-center gap-1">
                                <BarChart3 className="w-3 h-3 text-[#BA1826]" /> Impact metrics
                              </p>
                              <button onClick={() => setEditForm({
                                ...editForm,
                                impact_metrics: [...(editForm.impact_metrics || []),
                                  { id: `m-${Date.now()}`, label: '', unit: '', current_value: 0, target_value: '' }],
                              })}
                                className="text-[10px] text-[#BA1826] inline-flex items-center gap-1"
                                data-testid={`fund-metric-add-${f.code}`}>
                                <Plus className="w-3 h-3" /> Add metric
                              </button>
                            </div>
                            {(editForm.impact_metrics || []).length === 0 ? (
                              <p className="text-[10px] italic text-[#9CA3AF]">
                                No metrics yet. Track outcomes (e.g. "Meals distributed", "Kits assembled", "Certifications issued").
                              </p>
                            ) : (
                              <div className="space-y-1.5">
                                {(editForm.impact_metrics || []).map((m, idx) => (
                                  <div key={m.id || idx} className="grid grid-cols-12 gap-1.5 items-center"
                                    data-testid={`fund-metric-row-${f.code}-${idx}`}>
                                    <input value={m.label || ''}
                                      onChange={(e) => {
                                        const next = [...(editForm.impact_metrics || [])];
                                        next[idx] = { ...next[idx], label: e.target.value };
                                        setEditForm({ ...editForm, impact_metrics: next });
                                      }}
                                      placeholder="Metric (e.g. Meals served)"
                                      className="col-span-4 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                                    <input value={m.unit || ''}
                                      onChange={(e) => {
                                        const next = [...(editForm.impact_metrics || [])];
                                        next[idx] = { ...next[idx], unit: e.target.value };
                                        setEditForm({ ...editForm, impact_metrics: next });
                                      }}
                                      placeholder="Unit"
                                      className="col-span-2 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                                    <input type="number" min="0" step="any" value={m.current_value ?? 0}
                                      onChange={(e) => {
                                        const next = [...(editForm.impact_metrics || [])];
                                        next[idx] = { ...next[idx], current_value: e.target.value };
                                        setEditForm({ ...editForm, impact_metrics: next });
                                      }}
                                      placeholder="Current"
                                      className="col-span-2 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono" />
                                    <input type="number" min="0" step="any" value={m.target_value ?? ''}
                                      onChange={(e) => {
                                        const next = [...(editForm.impact_metrics || [])];
                                        next[idx] = { ...next[idx], target_value: e.target.value };
                                        setEditForm({ ...editForm, impact_metrics: next });
                                      }}
                                      placeholder="Target"
                                      className="col-span-2 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono" />
                                    <input value={m.notes || ''}
                                      onChange={(e) => {
                                        const next = [...(editForm.impact_metrics || [])];
                                        next[idx] = { ...next[idx], notes: e.target.value };
                                        setEditForm({ ...editForm, impact_metrics: next });
                                      }}
                                      placeholder="Notes (optional)"
                                      className="col-span-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs" />
                                    <button onClick={() => {
                                      const next = (editForm.impact_metrics || []).filter((_, j) => j !== idx);
                                      setEditForm({ ...editForm, impact_metrics: next });
                                    }} className="col-span-1 text-[#BA1826] flex justify-center"
                                      data-testid={`fund-metric-remove-${f.code}-${idx}`}
                                      title="Remove metric"><Trash2 className="w-3 h-3" /></button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditingId(null)}
                              className="text-[11px] px-3 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1">
                              <X className="w-3 h-3" /> Cancel
                            </button>
                            <button onClick={() => saveEdit(f.id)}
                              className="text-[11px] px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5"
                              data-testid={`fund-save-${f.code}`}>
                              <Save className="w-3 h-3" /> Save changes
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }
                const bp = bal.budget_progress_percent;
                return (
                  <tr key={f.id} className={`border-t border-[#E5E7EB] ${f.is_active ? '' : 'opacity-50'}`}
                    data-testid={`fund-row-${f.code}`}>
                    <td className="px-3 py-2 font-mono font-bold">
                      {f.code}
                      {f.default_for_unrestricted && (
                        <Star className="w-3 h-3 inline ml-1 text-[#BA1826]" title="Default unrestricted fund — untagged donations roll here." />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span>{f.name}</span>
                      {f.is_system && <Lock className="w-3 h-3 text-[#9CA3AF] inline ml-1" title="System-seeded fund (cannot be deleted)" />}
                      {f.description && <p className="text-[10px] text-[#9CA3AF] mt-0.5 truncate max-w-md">{f.description}</p>}
                      {f.budget_target && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <div className="w-32 h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                            <div className={`h-full ${bp >= 100 ? 'bg-emerald-500' : 'bg-[#BA1826]'}`}
                              style={{ width: `${Math.min(bp || 0, 100)}%` }} />
                          </div>
                          <span className="text-[9px] font-mono text-[#4B5563]" data-testid={`fund-progress-${f.code}`}>
                            {bp ? `${bp}%` : '0%'} of {fmtMoney(f.budget_target)}
                          </span>
                        </div>
                      )}
                      {(f.impact_metrics || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(f.impact_metrics || []).slice(0, 4).map((m, i) => (
                            <span key={i} className="text-[9px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded px-1.5 py-0.5"
                              data-testid={`fund-metric-chip-${f.code}-${i}`}>
                              {m.label}: {m.current_value || 0}{m.target_value ? ` / ${m.target_value}` : ''} {m.unit || ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${FUND_TYPE_BADGE[f.fund_type] || ''}`}>
                        {FUND_TYPE_LABEL[f.fund_type] || f.fund_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-green-700">{fmtMoney(bal.revenue)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtMoney(bal.expenses)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmtMoney(bal.available)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => downloadFundCsv(f)}
                        className="text-[10px] p-1 text-[#0A0A0A] hover:text-[#BA1826]"
                        title="Download CSV report for this fund"
                        data-testid={`fund-csv-${f.code}`}>
                        <FileSpreadsheet className="w-3 h-3" />
                      </button>
                      {f.fund_type === 'unrestricted' && !f.default_for_unrestricted && (
                        <button onClick={() => setDefault(f)}
                          className="text-[10px] p-1 text-[#BA1826] hover:text-[#9F1421]"
                          title="Set as default unrestricted fund"
                          data-testid={`fund-make-default-${f.code}`}>
                          <Star className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => { setEditingId(f.id); setEditForm({
                        name: f.name, fund_type: f.fund_type,
                        description: f.description || '',
                        budget_target: f.budget_target ?? '',
                        start_date: f.start_date || '',
                        end_date: f.end_date || '',
                        impact_metrics: f.impact_metrics || [],
                      }); }}
                        className="text-[10px] p-1 text-[#4B5563] hover:text-[#0A0A0A] ml-1"
                        data-testid={`fund-edit-${f.code}`}>
                        <Edit3 className="w-3 h-3" />
                      </button>
                      {!f.default_for_unrestricted && (
                        <button onClick={() => toggleActive(f)}
                          className={`text-[10px] p-1 ml-1 ${f.is_active ? 'text-amber-700' : 'text-green-700'}`}
                          title={f.is_active ? 'Deactivate' : 'Reactivate'}
                          data-testid={`fund-toggle-${f.code}`}>
                          {f.is_active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {funds.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[#9CA3AF] italic">No funds yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ACCENT = {
  green: 'bg-green-50 border-green-200 text-green-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  brand: 'bg-[#FFF8F8] border-[#BA1826]/30 text-[#BA1826]',
};

const Tile = ({ icon: Icon, label, value, accent = 'brand' }) => (
  <div className={`border rounded-lg p-3 ${ACCENT[accent]}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</span>
      <Icon className="w-3.5 h-3.5 opacity-70" />
    </div>
    <p className="font-heading text-xl font-bold truncate" title={value}>{value}</p>
  </div>
);

export default FundsTab;
